import React, { useState, useEffect, useRef, useMemo } from 'react';
import SocialLinksBubbles from './common/SocialLinksBubbles';
import LiveChessGame from './common/LiveChessGame';
import ChessPlatformBubbleTab from './common/ChessPlatformBubbleTab';
import DonationBubbleTab from './donations/DonationBubbleTab';
import ImageCarousel from './common/ImageCarousel';
import YouTubeCarousel from './common/YouTubeCarousel';
import LichessStudyCarousel from './common/LichessStudyCarousel';
import { Chess } from 'chess.js';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { fetchChessData } from '../services/chessApiService';
import { getPublishedBlogsByProfileId } from '../services/firestoreService';
import BlogSubscribeButton, { BlogSectionTitle } from './BlogSubscribeButton';
import ContactFormSection from './ContactFormSection';
import ShopPreviewSection from './ShopPreviewSection';
import { renderFormattedText } from '../utils/textFormatter';

const DEFAULT_ACCENT = '#749555';

// Utility functions
const clampChannel = (value) => Math.min(Math.max(value, 0), 255);

const normalizeHex = (hex) => {
  if (!hex) return null;
  let normalized = hex.replace('#', '');
  if (normalized.length === 3) {
    normalized = normalized.split('').map(char => char + char).join('');
  }
  if (normalized.length !== 6) {
    return null;
  }
  return normalized;
};

const adjustColor = (hex, amount = 0) => {
  const normalized = normalizeHex(hex);
  if (!normalized) return hex || DEFAULT_ACCENT;
  const num = parseInt(normalized, 16);
  let r = (num >> 16) & 255;
  let g = (num >> 8) & 255;
  let b = num & 255;

  r = clampChannel(r + Math.round(255 * amount));
  g = clampChannel(g + Math.round(255 * amount));
  b = clampChannel(b + Math.round(255 * amount));

  return `#${[r, g, b]
    .map(channel => channel.toString(16).padStart(2, '0'))
    .join('')}`;
};

const hexToRgba = (hex, alpha = 1) => {
  const normalized = normalizeHex(hex);
  if (!normalized) return `rgba(116, 149, 85, ${alpha})`;
  const num = parseInt(normalized, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Parse (*text*) into clickable links
const parseLinksFromText = (text, accentColor = DEFAULT_ACCENT) => {
  if (!text) return text;

  const linkPattern = /\(\*([^*]+)\*\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = linkPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const linkContent = match[1];
    let linkText = linkContent;
    let linkUrl = linkContent;

    const colonSplit = linkContent.split(': ');
    const pipeSplit = linkContent.split(' | ');

    if (colonSplit.length === 2) {
      linkText = colonSplit[0].trim();
      linkUrl = colonSplit[1].trim();
    } else if (pipeSplit.length === 2) {
      linkText = pipeSplit[0].trim();
      linkUrl = pipeSplit[1].trim();
    }

    const isValidUrl = /^https?:\/\/.+/i.test(linkUrl);

    if (isValidUrl) {
      parts.push(
        <a
          key={match.index}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium"
          style={{ color: accentColor }}
        >
          {linkText}
        </a>
      );
    } else {
      parts.push(match[0]);
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 1 ? parts : text;
};

// Blog Section Component
const BlogSection = ({ profileId, username, accentColor = DEFAULT_ACCENT, onBlogsLoaded, animate = false, isOwner = false }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        const fetchedBlogs = await getPublishedBlogsByProfileId(profileId);
        const sortedBlogs = fetchedBlogs.sort((a, b) => {
          if (a.pinned && !b.pinned) return -1;
          if (!a.pinned && b.pinned) return 1;
          const aTime = (a.publishedAt?.seconds ?? a.createdAt?.seconds) || 0;
          const bTime = (b.publishedAt?.seconds ?? b.createdAt?.seconds) || 0;
          return bTime - aTime;
        });
        setBlogs(sortedBlogs);
        if (onBlogsLoaded) onBlogsLoaded(sortedBlogs.length > 0);
      } catch (error) {
        console.error('Error loading blogs:', error);
        if (onBlogsLoaded) onBlogsLoaded(false);
      } finally {
        setLoading(false);
      }
    };

    if (profileId) loadBlogs();
  }, [profileId, onBlogsLoaded]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleBlogClick = (blog) => {
    window.open(`/blog/${blog.authorUsername}/${blog.slug}`, '_blank');
  };

  const handleSeeAllBlogs = () => {
    if (username) window.open(`/blogs/author/${username}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center space-y-6 text-center py-8">
        <div className="relative">
          <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center animate-pulse">
            <i className="fas fa-chess-king text-white/30 text-3xl"></i>
          </div>
        </div>
      </div>
    );
  }

  if (blogs.length === 0) return null;

  const displayedBlogs = blogs.slice(0, 3);
  const hasMoreBlogs = blogs.length >= 4;

  return (
    <div className="space-y-4">
      {displayedBlogs.map((blog) => (
        <div
          key={blog.id}
          onClick={() => handleBlogClick(blog)}
          className="rounded-lg shadow-md hover:shadow-xl transition-all cursor-pointer overflow-hidden border-2 w-full max-w-2xl mx-auto"
          style={{ borderColor: accentColor }}
        >
          <div className="flex flex-row min-h-[180px]">
            <div className={`${isMobile ? 'w-36' : 'w-56'} flex-shrink-0 flex flex-col bg-gray-900`}>
              <div className="aspect-video overflow-hidden">
                <img
                  src={blog.imageUrl || '/Images/Blog.png'}
                  alt={blog.title}
                  className="w-full h-full object-cover bg-gray-900"
                  loading="lazy"
                />
              </div>
              <div className="flex-1 flex items-center justify-center bg-gray-800 py-3 border-t border-gray-700">
                <div className={`flex items-center text-blue-400 font-semibold ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  <span>Read More</span>
                  <i className="fas fa-arrow-right ml-2"></i>
                </div>
              </div>
            </div>

            <div className={`flex-1 ${isMobile ? 'p-3' : 'p-6'} flex flex-col justify-center`}>
              <h3 className={`${isMobile ? 'text-base' : 'text-xl'} font-bold text-white mb-2 hover:opacity-90 transition-colors line-clamp-2`}>
                {blog.title}
              </h3>
              <p className="text-gray-300 text-xs mb-3">
                {formatDate(blog.publishedAt || blog.createdAt)}
              </p>
              <p className={`text-gray-200 ${isMobile ? 'text-xs' : 'text-sm'} line-clamp-3`}>
                {blog.excerpt}
              </p>
            </div>
          </div>
        </div>
      ))}

      <div className="flex flex-col items-center gap-6 pt-6 w-full mb-8">
        <div className="w-full max-w-2xl mx-auto">
          <BlogSubscribeButton
            profileId={profileId}
            username={username}
            source="blog-preview"
            variant="compact"
            accentColor={accentColor}
            animate={animate}
            isOwner={isOwner}
          />
        </div>

        {hasMoreBlogs && (
          <button
            onClick={handleSeeAllBlogs}
            className="px-8 py-3 border-2 rounded-[10px] font-semibold transition-all shadow-lg text-white hover:scale-105 mb-4"
            style={{
              borderColor: accentColor,
              backgroundColor: 'rgba(116, 149, 85, 0.2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = accentColor;
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(116, 149, 85, 0.2)';
              e.currentTarget.style.color = 'white';
            }}
          >
            <i className="fas fa-newspaper mr-2"></i>
            See All @{username} Blogs
          </button>
        )}
      </div>
    </div>
  );
};

// Chess Ratings Section Component
const ChessRatingsSection = ({ chessComUsername, lichessUsername, fideRating, nationalRating, federation, showRatings = false }) => {
  const [chessComData, setChessComData] = useState(null);
  const [lichessData, setLichessData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadRatings = async () => {
      if (!showRatings || (!chessComUsername && !lichessUsername && !fideRating && !nationalRating)) return;

      setLoading(true);

      if (chessComUsername) {
        try {
          const data = await fetchChessData('chessCom', chessComUsername);
          setChessComData(data);
        } catch (error) {
          console.error('Error fetching Chess.com data:', error);
        }
      }

      if (lichessUsername) {
        try {
          const data = await fetchChessData('lichess', lichessUsername);
          setLichessData(data);
        } catch (error) {
          console.error('Error fetching Lichess data:', error);
        }
      }

      setLoading(false);
    };

    loadRatings();
  }, [chessComUsername, lichessUsername, fideRating, nationalRating, showRatings]);

  const hasAnyRating = fideRating || nationalRating || (chessComData?.rating && chessComData.rating > 0) || (lichessData?.rating && lichessData.rating > 0);

  if (!showRatings || (!hasAnyRating && !loading)) {
    return null;
  }

  // Count visible ratings for dynamic grid layout
  let ratingCount = 0;
  if (fideRating || nationalRating) ratingCount++;
  if (chessComData?.rating && chessComData.rating > 0) ratingCount++;
  if (lichessData?.rating && lichessData.rating > 0) ratingCount++;

  // Dynamic grid class based on number of ratings
  const gridClass = ratingCount === 1
    ? 'grid grid-cols-1 gap-3'
    : ratingCount === 2
      ? 'grid grid-cols-1 sm:grid-cols-2 gap-3'
      : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3';

  return (
    <div className="mt-8">
      {/* <div className="flex items-center mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mr-3 shadow-lg">
          <i className="fas fa-chess text-white text-lg"></i>
        </div>
        <h4 className="text-2xl font-bold text-white">Chess Ratings</h4>
      </div> */}

      {loading ? (
        <div className="bg-gradient-to-br from-blue-900/30 to-indigo-900/30 rounded-xl p-8 text-center border border-blue-500">
          <div className="w-12 h-12 bg-blue-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-spinner fa-spin text-blue-400 text-xl"></i>
          </div>
          <p className="text-blue-300 font-medium">Loading ratings...</p>
        </div>
      ) : (
        <div className={gridClass}>
          {fideRating ? (
            <div className="bg-gray-800 rounded-xl p-3 border-2 border-gray-700 hover:border-gray-600 transition-all duration-200 hover:shadow-lg group">
              <div className="text-center mb-2">
                <div className="w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-1">
                  <i className="fas fa-globe text-gray-300 text-lg"></i>
                </div>
                <div className="font-bold text-gray-100 text-lg">FIDE</div>
                <div className="bg-gray-700 text-gray-200 px-2 py-1 rounded-full text-xs font-semibold mt-1 inline-block">
                  Official
                </div>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-100 mb-0">{fideRating}</p>
                <p className="text-gray-300 text-sm font-medium">Official Rating</p>
              </div>
            </div>
          ) : nationalRating && (
            <div className="bg-gradient-to-br from-purple-900/30 to-violet-900/30 rounded-xl p-3 border-2 border-purple-500 hover:border-purple-400 transition-all duration-200 hover:shadow-lg group">
              <div className="text-center mb-2">
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center mx-auto mb-1">
                  <i className="fas fa-globe text-purple-400 text-lg"></i>
                </div>
                <div className="font-bold text-gray-100 text-lg">{federation || 'Federation'}</div>
                <div className="bg-gray-800 text-gray-200 px-2 py-1 rounded-full text-xs font-semibold mt-1 inline-block">
                  National
                </div>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-400 mb-0">{nationalRating}</p>
                <p className="text-purple-300 text-sm font-medium">National Rating</p>
              </div>
            </div>
          )}

          {chessComData?.rating && chessComData.rating > 0 && (
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-3 border-2 border-chess-green hover:border-chess-green-dark transition-all duration-200 hover:shadow-lg group">
              <div className="text-center mb-2">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-1">
                  <img src="/Images/Chesscom.png" alt="Chess.com" className="w-10 h-10 rounded-lg" loading="lazy" />
                </div>
                <div className="font-bold text-white text-lg">Chess.com</div>
                {chessComData.ratingType && (
                  <div className="bg-chess-green text-white px-2 py-1 rounded-full text-xs font-semibold mt-1 inline-block">
                    {chessComData.ratingType}
                  </div>
                )}
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-chess-green mb-0">{chessComData.rating}</p>
                <p className="text-gray-300 text-sm font-medium">Current Rating</p>
              </div>
            </div>
          )}

          {lichessData?.rating && lichessData.rating > 0 && (
            <div className="bg-gradient-to-br from-gray-800 to-black rounded-xl p-3 border-2 border-gray-700 transition-all duration-200 group">
              <div className="text-center mb-2">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-1">
                  <img src="/Images/LiChess.png" alt="Lichess" className="w-10 h-10 rounded-lg" loading="lazy" />
                </div>
                <div className="font-bold text-white text-lg">Lichess.org</div>
                {lichessData.ratingType && (
                  <div className="bg-white text-gray-800 px-2 py-1 rounded-full text-xs font-semibold mt-1 inline-block">
                    {lichessData.ratingType}
                  </div>
                )}
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-white mb-0">{lichessData.rating}</p>
                <p className="text-white text-opacity-80 text-sm font-medium">Current Rating</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PlayerProfilePreviewTabs = ({ profileData, toggles, isPublicView = false, profileId, openDonationOnMount = false, isEditorPreview = false }) => {
  const { currentUser } = useAuth();

  // Check if current user is the profile owner
  const isOwner = Boolean(
    currentUser?.uid &&
    (profileData?.userId === currentUser.uid || profileData?.ownerId === currentUser.uid)
  );

  const [activeTab, setActiveTab] = useState('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeChessPlatform, setActiveChessPlatform] = useState(null);
  const [activeDonation, setActiveDonation] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');
  const [hasBlogsToSubscribe, setHasBlogsToSubscribe] = useState(false);
  const [isUserTurn, setIsUserTurn] = useState(false);
  const [chessGamePosition, setChessGamePosition] = useState(null);
  const [showTransitionState, setShowTransitionState] = useState(false);
  const [previousUserTurn, setPreviousUserTurn] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const blogSubscribeRef = useRef(null);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const primaryColor = profileData?.theme?.primaryColor || DEFAULT_ACCENT;

  const titleFullNames = {
    'GM': 'Grandmaster',
    'IM': 'International Master',
    'FM': 'FIDE Master',
    'CM': 'Candidate Master',
    'WGM': 'Woman Grandmaster',
    'WIM': 'Woman International Master',
    'WFM': 'Woman FIDE Master',
    'WCM': 'Woman Candidate Master'
  };

  // Profile type configuration
  const profileTypeConfig = {
    coach: { label: 'Coach', icon: 'fa-chalkboard-teacher' },
    creator: { label: 'Creator', icon: 'fa-video' },
    videoEditor: { label: 'Editor', icon: 'fa-film' },
    photographer: { label: 'Photographer', icon: 'fa-camera' },
    streamer: { label: 'Streamer', icon: 'fa-broadcast-tower' },
    organizer: { label: 'Tournament Organizer', icon: 'fa-trophy' },
    arbiter: { label: 'Arbiter', icon: 'fa-gavel' },
    commentator: { label: 'Commentator', icon: 'fa-microphone' },
    analyst: { label: 'Analyst', icon: 'fa-chart-line' },
    writer: { label: 'Journalist', icon: 'fa-pen' },
    puzzleCreator: { label: 'Puzzler', icon: 'fa-puzzle-piece' },
    openingTheorist: { label: 'Openings', icon: 'fa-chess' },
    endgameSpecialist: { label: 'Endgames', icon: 'fa-chess-king' },
    tactician: { label: 'Tactician', icon: 'fa-chess-knight' }
  };

  const profileType = profileData?.theme?.profileType || '';

  // Enhanced toggles for backward compatibility
  const enhancedToggles = useMemo(() => ({
    ...toggles,
    showPhotos: toggles.showPhotos !== undefined ? toggles.showPhotos : (toggles.showMedia !== undefined ? toggles.showMedia : true),
    showYouTube: toggles.showYouTube !== undefined ? toggles.showYouTube : (toggles.showMedia !== undefined ? toggles.showMedia : true),
    showLichessStudies: toggles.showLichessStudies !== undefined ? toggles.showLichessStudies : false
  }), [toggles]);

  // Define available tabs based on content and toggles
  const sections = [
    { id: 'home', label: 'Home', icon: 'fa-home', show: enhancedToggles.showBasicInfo || enhancedToggles.showYouTube || enhancedToggles.showPhotos || enhancedToggles.showLichessStudies },
    { id: 'about', label: 'About Me', icon: 'fa-info-circle', show: enhancedToggles.showChessDetails },
    { id: 'achievements', label: 'Achievements', icon: 'fa-trophy', show: enhancedToggles.showAchievements && profileData.achievements?.length > 0 },
    { id: 'coaching', label: 'Coaching', icon: 'fa-chalkboard-teacher', show: enhancedToggles.showCoaching && profileData.coaching?.available },
    { id: 'photos', label: 'Photos', icon: 'fa-camera', show: enhancedToggles.showPhotos && profileData.showPhotosAsTab === true && (profileData.photos?.length > 0 || profileData.media?.filter(item => item.type === 'uploaded_image' || item.type === 'image').length > 0) },
    { id: 'youtube', label: 'Videos', icon: 'fa-circle-play', show: enhancedToggles.showYouTube && profileData.showYoutubeAsTab === true && (profileData.youtubeVideos?.length > 0 || profileData.media?.filter(item => item.type === 'video').length > 0) },
    { id: 'lichessStudies', label: 'Games & Studies', icon: 'fa-chess-board', show: enhancedToggles.showLichessStudies && profileData.showLichessStudiesAsTab === true && profileData.lichessStudies?.length > 0 },
    { id: 'goals', label: 'Goals & Fun', icon: 'fa-flag-checkered', show: enhancedToggles.showGoals },
    // REMOVED: chessGame tab - now shown at top of Home tab when it's user's turn
    { id: 'blog', label: 'Blog', icon: 'fa-newspaper', show: enhancedToggles.showBlog },
    { id: 'affiliateLinks', label: 'Affiliates', icon: 'fa-link', show: (() => {
      const activeLinks = profileData.affiliateLinks?.filter(link => link.isActive) || [];
      return enhancedToggles.showAffiliateLinks && profileData.showAffiliateLinksAsTab === true && activeLinks.length > 0;
    })() },
    { id: 'shop', label: 'Shop', icon: 'fa-shopping-cart', show: profileData.playerPremium && enhancedToggles.showShop && profileData.showShopAsTab === true },
    { id: 'contact', label: 'Contact', icon: 'fa-envelope', show: profileData.playerPremium && enhancedToggles.showMessages },
    // REMOVED: referral - now shown in Home tab and More tab instead of separate tab
  ];

  // Add custom sections - SEPARATE from main nav
  const customSections = profileData.customSections || {};
  const customSectionsList = Object.keys(customSections).map(sectionId => {
    const section = customSections[sectionId];
    const icon = section?.icon || (section?.type === 'link' ? 'fa-link' : 'fa-info-circle');
    return {
      id: `custom_${sectionId}`,
      label: section?.title || `Custom ${sectionId.slice(-1)}`,
      icon: icon,
      show: enhancedToggles[`showCustom${sectionId}`] !== false,
      isCustom: true,
      sectionId,
      data: section
    };
  }).filter(section => section.show);

  // Add "More" tab if custom sections exist
  const moreTab = customSectionsList.length > 0 ? {
    id: 'more',
    label: 'More',
    icon: 'fa-ellipsis-h',
    show: true
  } : null;

  // Combine standard tabs + More tab (custom sections NOT in main nav)
  const allSections = moreTab ? [...sections, moreTab] : sections;
  const visibleTabs = allSections.filter(section => section.show);

  // Sort sections based on saved order - ALWAYS put Home first in tab layout
  const getSortedSections = (sectionsToSort) => {
    const sectionOrder = profileData.sectionOrder;

    // ALWAYS put Home first in tab layout
    const homeSection = sectionsToSort.find(s => s.id === 'home');
    const otherSections = sectionsToSort.filter(s => s.id !== 'home');

    if (!sectionOrder || sectionOrder.length === 0) {
      // Default order: Home → Chess Game → Others → More
      const sorted = otherSections.sort((a, b) => {
        if (a.id === 'chessGame') return -1;
        if (b.id === 'chessGame') return 1;
        // Keep "More" at the end
        if (a.id === 'more') return 1;
        if (b.id === 'more') return -1;
        return 0;
      });

      return homeSection ? [homeSection, ...sorted] : sorted;
    }

    // Custom order: Home first, then follow sectionOrder for others
    const orderedSections = [];
    const sectionsById = {};

    otherSections.forEach(section => {
      sectionsById[section.id] = section;
    });

    // Add sections in specified order (excluding home)
    sectionOrder.filter(id => id !== 'home').forEach(sectionId => {
      if (sectionsById[sectionId]) {
        orderedSections.push(sectionsById[sectionId]);
        delete sectionsById[sectionId];
      }
    });

    // Add any remaining sections (new sections not in order yet)
    Object.values(sectionsById).forEach(section => {
      if (section.id === 'chessGame') {
        // Insert chess game at beginning of "others"
        orderedSections.unshift(section);
      } else if (section.id === 'more') {
        // Insert "More" at end
        orderedSections.push(section);
      } else {
        // Insert other standard sections at end
        orderedSections.push(section);
      }
    });

    // Return with Home first
    return homeSection ? [homeSection, ...orderedSections] : orderedSections;
  };

  // Apply sorting to visible tabs
  const sortedTabs = useMemo(() => getSortedSections(visibleTabs), [
    visibleTabs,
    profileData.sectionOrder
  ]);

  // Auto-select first visible tab
  useEffect(() => {
    if (sortedTabs.length > 0 && !sortedTabs.find(tab => tab.id === activeTab)) {
      setActiveTab(sortedTabs[0].id);
    }
  }, [sortedTabs, activeTab]);

  // Check for blogs on mount to determine if subscribe button should show
  useEffect(() => {
    const checkForBlogs = async () => {
      if (profileId && enhancedToggles.showBlog) {
        try {
          const blogs = await getPublishedBlogsByProfileId(profileId);
          setHasBlogsToSubscribe(blogs && blogs.length > 0);
        } catch (error) {
          console.error('Error checking for blogs:', error);
          setHasBlogsToSubscribe(false);
        }
      }
    };
    checkForBlogs();
  }, [profileId, enhancedToggles.showBlog]);

  // Handle transition when user turn changes (keep chess visible for 2 seconds after move)
  useEffect(() => {
    // Check if user just stopped being their turn (made a move)
    if (previousUserTurn && !isUserTurn) {
      // User just made a move - show transition state
      setShowTransitionState(true);

      setTimeout(() => {
        // After 2s, hide transition state
        setShowTransitionState(false);
      }, 2000);
    }

    // Update previous turn for next comparison
    setPreviousUserTurn(isUserTurn);
  }, [isUserTurn, previousUserTurn]);

  // Monitor chess game for user's turn
  useEffect(() => {
    const actualProfileId = profileId || profileData.id;

    if (!actualProfileId || !profileData.chessGame?.enabled || !enhancedToggles.showChessGame) {
      setIsUserTurn(false);
      return;
    }

    const gameDocRef = doc(db, 'playerProfiles', actualProfileId, 'chessGames', 'current');

    const unsubscribe = onSnapshot(gameDocRef, (gameDoc) => {
      try {
        if (gameDoc.exists()) {
          const gameData = gameDoc.data();
          if (gameData.pgn) {
            const chess = new Chess();
            chess.loadPgn(gameData.pgn);
            const currentTurn = chess.turn(); // 'w' for white, 'b' for black
            setChessGamePosition(chess.fen());

            // Determine if it's the current user's turn
            const isProfileOwner = (currentUser?.uid === profileData.userId) ||
                                  (actualProfileId && localStorage.getItem('ownerAuth_' + actualProfileId) === 'true');

            const ownerColor = profileData.chessGame?.ownerColor || 'white';
            const userColor = isProfileOwner ? ownerColor : (ownerColor === 'white' ? 'black' : 'white');
            const userColorCode = userColor === 'white' ? 'w' : 'b';

            // Check if it's the user's turn and game is not over
            const isGameOver = chess.isGameOver();
            const isUsersTurn = currentTurn === userColorCode && !isGameOver;

            // Update the current turn state
            setIsUserTurn(isUsersTurn);
            setChessGamePosition(chess.fen());
          } else {
            // New game - check if user should start
            const isProfileOwner = (currentUser?.uid === profileData.userId) ||
                                  (actualProfileId && localStorage.getItem('ownerAuth_' + actualProfileId) === 'true');
            const ownerColor = profileData.chessGame?.ownerColor || 'white';
            const userColor = isProfileOwner ? ownerColor : (ownerColor === 'white' ? 'black' : 'white');

            // New game - white starts first
            setIsUserTurn(userColor === 'white');
          }
        } else {
          // No game exists yet - check if user should start
          const isProfileOwner = currentUser?.uid === profileData.userId ||
                                (actualProfileId && localStorage.getItem('ownerAuth_' + actualProfileId) === 'true');
          const ownerColor = profileData.chessGame?.ownerColor || 'white';
          const userColor = isProfileOwner ? ownerColor : (ownerColor === 'white' ? 'black' : 'white');

          // New game - white starts first
          setIsUserTurn(userColor === 'white');
        }
      } catch (error) {
        console.error('Error checking chess turn:', error);
        setIsUserTurn(false);
      }
    });

    return () => unsubscribe();
  }, [profileId, profileData.id, profileData.chessGame?.enabled, profileData.chessGame?.ownerColor, profileData.userId, enhancedToggles.showChessGame, currentUser?.uid]);

  // Auto-open donation bubble if route specifies it
  useEffect(() => {
    if (openDonationOnMount) {
      setActiveDonation(true);
    }
  }, [openDonationOnMount]);

  const handleCopyLink = async () => {
    const currentUrl = window.location.href;
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopyFeedback('Link copied!');
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (err) {
      setCopyFeedback('Failed to copy');
      setTimeout(() => setCopyFeedback(''), 2000);
    }
  };

  const handleDonationClick = () => {
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Open donation bubble
    setActiveDonation(true);
  };

  const formatUrl = (url) => {
    if (!url) return url;
    if (!url.match(/^https?:\/\//)) {
      return `https://${url}`;
    }
    return url;
  };

  const handleChessPlatformClick = (platform) => {
    setActiveChessPlatform(platform);
  };

  const handleScrollToSubscribe = () => {
    // Switch to blog tab
    setActiveTab('blog');

    // Wait for tab to render, then scroll to subscribe section
    setTimeout(() => {
      if (blogSubscribeRef.current) {
        blogSubscribeRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Tab content renderers
  const renderHomeTab = () => {
    const sectionOrder = profileData.sectionOrder || [];

    // Find About Me index - use the FIRST valid index (not the last)
    const aboutIndices = [
      sectionOrder.indexOf('about'),
      sectionOrder.indexOf('chessDetails'),
      sectionOrder.indexOf('basicInfo')
    ].filter(idx => idx !== -1);

    const aboutIndex = aboutIndices.length > 0 ? Math.min(...aboutIndices) : -1;

    // Prepare YouTube data
    const youtubeIndex = sectionOrder.indexOf('youtube');
    const youtubeVideos = profileData.youtubeVideos || [];
    const legacyVideos = (profileData.media || []).filter(item => item.type === 'video');
    const hasYoutubeContent = youtubeVideos.length > 0 || legacyVideos.length > 0;
    const showYoutubeOnHome = enhancedToggles.showYouTube && hasYoutubeContent && !profileData.showYoutubeAsTab;

    // Prepare Photos data
    const photosIndex = sectionOrder.indexOf('photos');
    const photos = profileData.photos || [];
    const legacyPhotos = (profileData.media || []).filter(item => item.type === 'uploaded_image' || item.type === 'image');
    const hasPhotosContent = photos.length > 0 || legacyPhotos.length > 0;
    const showPhotosOnHome = enhancedToggles.showPhotos && hasPhotosContent && !profileData.showPhotosAsTab;

    // Prepare Lichess Studies data
    const lichessStudiesIndex = sectionOrder.indexOf('lichessStudies');
    const lichessStudies = profileData.lichessStudies || [];
    const hasLichessStudiesContent = lichessStudies.length > 0;
    const showLichessStudiesOnHome = enhancedToggles.showLichessStudies && hasLichessStudiesContent;

    // Prepare Referral data
    const referralIndex = sectionOrder.indexOf('referral');
    const showReferral = enhancedToggles.showReferral && profileData.referral?.enabled;

    // Prepare Chess Ratings data
    const chessDetailsIndex = sectionOrder.indexOf('chessDetails');
    const showChessRatings = enhancedToggles.showChessRatings;
    const hasChessRatings = profileData.links?.chessCom || profileData.links?.lichess ||
                            profileData.fideRating || profileData.nationalRating;

    // Prepare Shop data
    const shopIndex = sectionOrder.indexOf('shop');
    const showShop = enhancedToggles.showShop && profileData.playerPremium;

    // Prepare Affiliate Links data
    const affiliateLinksIndex = sectionOrder.indexOf('affiliateLinks');
    const showAffiliateLinks = enhancedToggles.showAffiliateLinks && (profileData.affiliateLinks?.filter(link => link.isActive).length > 0);

    // Build a single sorted array of all sections to render
    const allSections = [];

    // Add About Me section
    if (profileData.bio && aboutIndex !== -1) {
      allSections.push({ id: 'about', type: 'about', orderIndex: aboutIndex });
    }

    // Add YouTube section (only if not showing as separate tab)
    if (showYoutubeOnHome && youtubeIndex !== -1) {
      allSections.push({ id: 'youtube', type: 'youtube', orderIndex: youtubeIndex });
    }

    // Add Photos section (only if not showing as separate tab)
    if (showPhotosOnHome && photosIndex !== -1) {
      allSections.push({ id: 'photos', type: 'photos', orderIndex: photosIndex });
    }

    // Add Lichess Studies section (only if not showing as separate tab)
    if (showLichessStudiesOnHome) {
      allSections.push({ id: 'lichessStudies', type: 'lichessStudies', orderIndex: lichessStudiesIndex !== -1 ? lichessStudiesIndex : 999 });
    }

    // Add Chess Ratings section (independent from About Me)
    if (showChessRatings && hasChessRatings && chessDetailsIndex !== -1) {
      allSections.push({ id: 'chessRatings', type: 'chessRatings', orderIndex: chessDetailsIndex });
    }

    // Add Referral section
    if (showReferral && referralIndex !== -1) {
      allSections.push({ id: 'referral', type: 'referral', orderIndex: referralIndex });
    }

    // Add Shop section (use fallback orderIndex if not in sectionOrder)
    if (showShop) {
      allSections.push({ id: 'shop', type: 'shop', orderIndex: shopIndex !== -1 ? shopIndex : 999 });
    }

    // Add Affiliate Links section (only if not showing as separate tab)
    if (showAffiliateLinks && !profileData.showAffiliateLinksAsTab) {
      allSections.push({ id: 'affiliateLinks', type: 'affiliateLinks', orderIndex: affiliateLinksIndex !== -1 ? affiliateLinksIndex : 999 });
    }

    // Add custom sections
    customSectionsList.forEach(cs => {
      const customIndex = sectionOrder.indexOf(cs.id);
      if (customIndex !== -1) {
        allSections.push({ ...cs, type: 'custom', orderIndex: customIndex });
      }
    });

    // Sort all sections by their order index
    allSections.sort((a, b) => a.orderIndex - b.orderIndex);

    return (
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Chess Game - At top when user's turn or during transition */}
        {(isUserTurn || showTransitionState) && enhancedToggles.showChessGame && profileData.chessGame?.enabled && (
          <div className="mb-8">
            <div className="w-full max-w-2xl mx-auto">
              <LiveChessGame
                profileId={profileId || profileData.id}
                profileOwnerId={profileData.ownerId || profileData.userId}
                chessGameConfig={profileData.chessGame}
                isOwnerView={!isPublicView}
                accentColor={primaryColor}
                profileUsername={profileData.username || profileData.slug || ''}
                profileDisplayName={profileData.displayName || ''}
                showTurnBanner={isUserTurn || showTransitionState}
                isTransitioning={showTransitionState}
                title={profileData.chessGame?.title || 'Play Chess With Me'}
              />
            </div>
          </div>
        )}

        {/* All sections in order */}
        {allSections.map((section) => {
          if (section.type === 'about') {
            // Render About Me section
            return (
              <div key="about" className="space-y-8">
                <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-sm p-6 sm:p-8">
                  <h3 className="text-xl font-semibold text-center mb-4 text-white">About Me</h3>
                  <p className="text-gray-200 whitespace-pre-wrap leading-relaxed text-lg">
                    {parseLinksFromText(profileData.bio, primaryColor)}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(profileData.location || profileData.country) && (
                    <div className="bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-700">
                      <div className="flex items-center text-gray-300">
                        <i className="fas fa-map-marker-alt mr-3 text-red-400 text-xl"></i>
                        <div>
                          <span className="font-bold text-white">Location:</span>
                          <span className="ml-2 text-gray-300">
                            {profileData.location || [profileData.city, profileData.country].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {profileData.email && (
                    <div className="bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-700">
                      <div className="flex items-center text-gray-300">
                        <i className="fas fa-envelope mr-3 text-blue-400 text-xl"></i>
                        <div>
                          <span className="font-bold text-white">Email:</span>
                          <a
                            href={`mailto:${profileData.email}`}
                            className="ml-2 text-blue-400 hover:underline"
                          >
                            {profileData.email}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          } else if (section.type === 'youtube') {
            // Render YouTube carousel
            const allVideos = youtubeVideos.length > 0 ? youtubeVideos : legacyVideos;
            return (
              <div key="youtube" className="w-full">
                <YouTubeCarousel
                  videos={allVideos}
                  className=""
                  showDots={true}
                  showArrows={true}
                  accentColor={primaryColor}
                  layout={profileData?.theme?.layout}
                />
              </div>
            );
          } else if (section.type === 'photos') {
            // Render Photos carousel
            const allPhotos = photos.length > 0 ? photos : legacyPhotos;
            return (
              <div key="photos" className="w-full">
                <ImageCarousel
                  images={allPhotos}
                  className=""
                  showDots={true}
                  showArrows={true}
                  autoHeight={true}
                  accentColor={primaryColor}
                  layout={profileData?.theme?.layout}
                />
              </div>
            );
          } else if (section.type === 'lichessStudies') {
            // Render Lichess Studies carousel
            return (
              <div key="lichessStudies" className="w-full">
                <LichessStudyCarousel
                  studies={lichessStudies}
                  className=""
                  showDots={true}
                  showArrows={true}
                  accentColor={primaryColor}
                  layout={profileData?.theme?.layout}
                  boardTheme={profileData?.lichessBoardTheme || 'brown'}
                />
              </div>
            );
          } else if (section.type === 'chessRatings') {
            // Render Chess Ratings section
            return (
              <div key="chessRatings">
                <ChessRatingsSection
                  chessComUsername={profileData.links?.chessCom}
                  lichessUsername={profileData.links?.lichess}
                  fideRating={profileData.fideRating}
                  nationalRating={profileData.nationalRating}
                  federation={profileData.federation}
                  showRatings={enhancedToggles.showChessRatings}
                />
              </div>
            );
          } else if (section.type === 'referral') {
            // Render referral card
            return (
              <a
                key="referral"
                href={`${typeof window !== 'undefined' ? window.location.origin : 'https://chessweb.site'}/me?ref=${profileData.userId || profileData.id}`}
                className="block w-full bg-gray-800 hover:bg-gray-700 rounded-lg p-4 shadow-md border-2 transition-all group"
                style={{ borderColor: primaryColor }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <i className="fas fa-rocket text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white group-hover:opacity-90 transition">
                        {profileData.referral?.ctaText || 'Create Your Page'}
                      </h4>
                    </div>
                  </div>
                  <i className="fas fa-arrow-right text-gray-400 group-hover:text-white transition"></i>
                </div>
              </a>
            );
          } else if (section.type === 'shop') {
            // Render Shop section
            return (
              <div key="shop">
                <ShopPreviewSection
                  profileId={profileId || profileData.id}
                  vendorId={profileData?.userId}
                  accentColor={primaryColor}
                />
              </div>
            );
          } else if (section.type === 'affiliateLinks') {
            // Render Affiliate Links section
            const affiliateLinks = profileData.affiliateLinks || [];
            const activeLinks = affiliateLinks.filter(link => link.isActive);

            const formatUrl = (url) => {
              if (!url) return url;
              if (!url.match(/^https?:\/\//)) {
                return `https://${url}`;
              }
              return url;
            };

            return (
              <div key="affiliateLinks" className="space-y-3">
                {activeLinks.map((link) => (
                  <a
                    key={link.id}
                    href={formatUrl(link.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-gray-800 hover:bg-gray-700 rounded-lg p-4 shadow-md border-2 transition-all group"
                    style={{ borderColor: primaryColor }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                          style={{ backgroundColor: primaryColor }}
                        >
                          <i className={`fas ${link.icon} text-lg`}></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-white group-hover:opacity-90 transition">
                            {link.title}
                          </h4>
                          {link.description && (
                            <p className="text-sm text-gray-400">{link.description}</p>
                          )}
                        </div>
                      </div>
                      <i className="fas fa-external-link-alt text-gray-400 group-hover:text-white transition"></i>
                    </div>
                  </a>
                ))}
              </div>
            );
          } else {
            // Render custom section card
            return (
              <button
                key={section.id}
                onClick={() => setActiveTab('more')}
                className="w-full bg-gray-800 hover:bg-gray-700 rounded-lg p-4 shadow-md border-2 transition-all text-left group"
                style={{ borderColor: primaryColor }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <i className={`fas ${section.icon} text-lg`}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white group-hover:opacity-90 transition">
                        {section.label}
                      </h4>
                    </div>
                  </div>
                  <i className="fas fa-arrow-right text-gray-400 group-hover:text-white transition"></i>
                </div>
              </button>
            );
          }
        })}
      </div>
    );
  };

  const renderAboutTab = () => (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      {/* Full Bio with Markdown */}
      {profileData.bio && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-sm p-6 sm:p-8">
          <h3 className="text-2xl font-semibold text-center mb-6 text-white">About Me</h3>
          <div className="text-gray-200 leading-relaxed text-lg prose prose-lg max-w-none">
            {renderFormattedText(profileData.bio, isMobile)}
          </div>
        </div>
      )}

      {/* Chess Details */}
      {(profileData.fideId || profileData.federation || profileData.federationId || profileData.fideRating || profileData.nationalRating) && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-sm p-6 sm:p-8 space-y-6">
          <h3 className="text-2xl font-semibold text-center mb-6 text-white">Chess Details</h3>

          <div className="grid grid-cols-2 gap-4">
            {profileData.fideId && (
              <div>
                <label className="block text-sm font-medium text-gray-400">FIDE ID</label>
                <p className="mt-1 text-lg text-white font-semibold">{profileData.fideId}</p>
              </div>
            )}
            {profileData.federation && (
              <div>
                <label className="block text-sm font-medium text-gray-400">Federation</label>
                <p className="mt-1 text-lg text-white font-semibold">{profileData.federation}</p>
              </div>
            )}
            {profileData.federationId && (
              <div>
                <label className="block text-sm font-medium text-gray-400">Federation ID</label>
                <p className="mt-1 text-lg text-white font-semibold">{profileData.federationId}</p>
              </div>
            )}
            {profileData.fideRating && (
              <div>
                <label className="block text-sm font-medium text-gray-400">FIDE Rating</label>
                <p className="mt-1 text-2xl font-bold text-blue-400">{profileData.fideRating}</p>
              </div>
            )}
            {profileData.nationalRating && (
              <div>
                <label className="block text-sm font-medium text-gray-400">National Rating</label>
                <p className="mt-1 text-2xl font-bold text-green-400">{profileData.nationalRating}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderAchievementsTab = () => {
    const achievements = profileData.achievements || [];

    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-4">
          {achievements.map((achievement, index) => (
            <div key={achievement.id || index} className="relative">
              {index < achievements.length - 1 && (
                <div className="absolute left-6 top-12 w-0.5 h-8 bg-gray-300"></div>
              )}

              <div className="flex items-start space-x-4">
                <div
                  className="text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg min-w-20 text-center flex-shrink-0"
                  style={{
                    backgroundColor: primaryColor,
                    boxShadow: `0 8px 20px ${hexToRgba(primaryColor, 0.35)}`
                  }}
                >
                  {achievement.year}
                </div>

                <div className="flex-1 bg-gray-800 border border-gray-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">
                        {achievement.achievement || achievement.title || 'Achievement'}
                      </h4>
                      {achievement.event && achievement.title && (
                        <p className="text-sm mb-2" style={{ color: primaryColor }}>
                          <i className="fas fa-calendar-alt mr-1"></i>
                          {achievement.event}
                        </p>
                      )}
                      {achievement.description && (
                        <p className="text-gray-300 text-sm leading-relaxed">
                          {achievement.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCoachingTab = () => {
    const coaching = profileData.coaching || {};

    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-sm p-6 sm:p-8 space-y-6">
          {coaching.description && (
            <div>
              <h4 className="font-bold text-white mb-2 text-lg">About My Coaching</h4>
              <p className="text-gray-200 whitespace-pre-wrap leading-relaxed">
                {parseLinksFromText(coaching.description, primaryColor)}
              </p>
            </div>
          )}

          {coaching.rates && (
            <div>
              <h4 className="font-bold text-white mb-2 text-lg">Rates & Pricing</h4>
              <p className="text-gray-200 whitespace-pre-wrap leading-relaxed">{coaching.rates}</p>
            </div>
          )}

          {coaching.specializations && coaching.specializations.length > 0 && (
            <div>
              <h4 className="font-bold text-white mb-3 text-lg">Specializations</h4>
              <div className="flex flex-wrap gap-2">
                {coaching.specializations.map((specialization, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 text-sm rounded-full font-medium text-white"
                    style={{
                      backgroundColor: hexToRgba(primaryColor, 0.8),
                      border: `1px solid ${hexToRgba(primaryColor, 1)}`
                    }}
                  >
                    {specialization}
                  </span>
                ))}
              </div>
            </div>
          )}

          {coaching.email && (
            <div className="pt-4 text-center">
              <a
                href={`mailto:${coaching.email}`}
                className="inline-flex items-center px-6 py-3 text-white rounded-lg transition-colors hover:opacity-95 font-semibold"
                style={{
                  backgroundColor: primaryColor,
                  boxShadow: `0 8px 20px ${hexToRgba(primaryColor, 0.3)}`
                }}
              >
                <i className="fas fa-envelope mr-2"></i>
                Contact for Coaching
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPhotosTab = () => {
    const photos = profileData.photos || [];
    const legacyMedia = profileData.media || [];
    const legacyPhotos = legacyMedia.filter(item => item.type === 'uploaded_image' || item.type === 'image');
    const allPhotos = photos.length > 0 ? photos : legacyPhotos;

    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <ImageCarousel
          images={allPhotos}
          className="max-w-2xl mx-auto"
          showDots={true}
          showArrows={true}
          autoHeight={true}
          accentColor={primaryColor}
        />
      </div>
    );
  };

  const renderYouTubeTab = () => {
    const youtubeVideos = profileData.youtubeVideos || [];
    const legacyMedia = profileData.media || [];
    const legacyVideos = legacyMedia.filter(item => item.type === 'video');
    const allVideos = youtubeVideos.length > 0 ? youtubeVideos : legacyVideos;

    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <YouTubeCarousel
          videos={allVideos}
          className="max-w-2xl mx-auto"
          showDots={true}
          showArrows={true}
          accentColor={primaryColor}
        />
      </div>
    );
  };

  const renderLichessStudiesTab = () => {
    const lichessStudies = profileData.lichessStudies || [];

    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div key="lichessStudies" className="w-full">
          <LichessStudyCarousel
            studies={lichessStudies}
            className=""
            showDots={true}
            showArrows={true}
            accentColor={primaryColor}
            layout={profileData?.theme?.layout}
            boardTheme={profileData?.lichessBoardTheme || 'brown'}
          />
        </div>
      </div>
    );
  };

  const renderGoalsTab = () => (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="space-y-6">
        {profileData.chessStory && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-sm p-6">
            <h4 className="font-bold text-white mb-3 text-lg">What's your chess story?</h4>
            <p className="text-gray-200 whitespace-pre-wrap leading-relaxed">{profileData.chessStory}</p>
          </div>
        )}

        {profileData.loveAboutChess && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-sm p-6">
            <h4 className="font-bold text-white mb-3 text-lg">What do you love most about chess?</h4>
            <p className="text-gray-200 whitespace-pre-wrap leading-relaxed">{profileData.loveAboutChess}</p>
          </div>
        )}

        {profileData.favoriteOpening && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-sm p-6">
            <h4 className="font-bold text-white mb-3 text-lg">What's your favorite opening?</h4>
            <p className="text-gray-200 whitespace-pre-wrap leading-relaxed">{profileData.favoriteOpening}</p>
          </div>
        )}

        {profileData.funChessFact && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-sm p-6">
            <h4 className="font-bold text-white mb-3 text-lg">Fun chess fact about you?</h4>
            <p className="text-gray-200 whitespace-pre-wrap leading-relaxed">{profileData.funChessFact}</p>
          </div>
        )}

        {profileData.chessGoals && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-sm p-6">
            <h4 className="font-bold text-white mb-3 text-lg">What are your chess goals?</h4>
            <p className="text-gray-200 whitespace-pre-wrap leading-relaxed">{profileData.chessGoals}</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderChessGameTab = () => {
    const actualProfileId = profileId || profileData.id;

    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <LiveChessGame
          profileId={actualProfileId}
          profileOwnerId={profileData.ownerId || profileData.userId}
          chessGameConfig={profileData.chessGame}
          isOwnerView={!isPublicView}
          accentColor={primaryColor}
          profileUsername={profileData.username || profileData.slug || ''}
          profileDisplayName={profileData.displayName || ''}
          showTurnBanner={false}
          isTransitioning={false}
          title={profileData.chessGame?.title || 'Play Chess With Me'}
        />
      </div>
    );
  };

  const renderBlogTab = () => (
    <div className="max-w-4xl mx-auto px-4 ">
      <BlogSectionTitle username={profileData?.displayName} accentColor={primaryColor} />
      <div ref={blogSubscribeRef}>
        <BlogSection
          profileId={profileId}
          username={profileData?.username}
          accentColor={primaryColor}
          onBlogsLoaded={setHasBlogsToSubscribe}
          isOwner={isOwner}
        />
      </div>
    </div>
  );

  const renderAffiliateLinksTab = () => {
    const affiliateLinks = profileData.affiliateLinks || [];
    const activeLinks = affiliateLinks.filter(link => link.isActive);

    const formatUrl = (url) => {
      if (!url) return url;
      if (!url.match(/^https?:\/\//)) {
        return `https://${url}`;
      }
      return url;
    };

    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-3">
          {activeLinks.map((link) => (
            <a
              key={link.id}
              href={formatUrl(link.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-gray-800 hover:bg-gray-700 rounded-lg p-4 shadow-md border-2 transition-all group"
              style={{ borderColor: primaryColor }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <i className={`fas ${link.icon} text-lg`}></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white group-hover:opacity-90 transition">
                      {link.title}
                    </h4>
                    {link.description && (
                      <p className="text-sm text-gray-400">{link.description}</p>
                    )}
                  </div>
                </div>
                <i className="fas fa-external-link-alt text-gray-400 group-hover:text-white transition"></i>
              </div>
            </a>
          ))}
        </div>
      </div>
    );
  };

  const renderShopTab = () => (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <ShopPreviewSection
        profileId={profileId}
        vendorId={profileData?.userId}
        accentColor={primaryColor}
      />
    </div>
  );

  const renderContactTab = () => (
    <div className="max-w-4xl mx-auto px-4">
      <ContactFormSection
        profileId={profileId}
        username={profileData?.username}
        isPremium={profileData?.playerPremium}
        accentColor={primaryColor}
        description={profileData?.contactDescription}
      />
    </div>
  );

  const renderReferralTab = () => {
    const referralConfig = profileData.referral || {};
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://chessweb.site';
    const referralUrl = `${origin}/me?ref=${profileData.userId || profileData.id}`;

    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-sm p-8 text-center space-y-6">
          <div className="mb-4">
            <i className="fas fa-rocket text-5xl md:text-6xl" style={{ color: primaryColor }}></i>
          </div>

          <h4 className="text-lg md:text-xl font-semibold text-white px-4">
            {referralConfig.description || 'Build your own professional chess profile in minutes - completely free!'}
          </h4>

          <a
            href={referralUrl}
            className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-4 text-white rounded-lg text-lg font-semibold transition-all hover:opacity-90 shadow-lg"
            style={{
              backgroundColor: referralConfig.buttonColor || primaryColor,
              boxShadow: `0 8px 20px ${hexToRgba(referralConfig.buttonColor || primaryColor, 0.3)}`
            }}
          >
            <i className="fas fa-plus-circle mr-2"></i>
            Get Started Free
          </a>

          <p className="text-sm text-gray-300 px-4">
            Join {profileData.displayName || 'me'} and create your chess profile
          </p>
        </div>
      </div>
    );
  };

  const renderCustomTab = (sectionData) => {
    if (!sectionData) {
      return (
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-sm p-8 text-center">
            <i className="fas fa-puzzle-piece text-4xl text-gray-400 mb-4"></i>
            <p className="text-gray-400">Custom section is being configured.</p>
          </div>
        </div>
      );
    }

    if (sectionData.type === 'link') {
      return (
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-sm p-8 text-center space-y-4">
            <p className="text-gray-300 mb-4">This section links to an external page.</p>
            <a
              href={formatUrl(sectionData.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 text-white rounded-lg transition-colors hover:opacity-95 font-semibold"
              style={{
                backgroundColor: primaryColor,
                boxShadow: `0 8px 20px ${hexToRgba(primaryColor, 0.3)}`
              }}
            >
              <i className="fas fa-external-link-alt mr-2"></i>
              Visit Link
            </a>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-sm p-6 sm:p-8 space-y-4">
          {sectionData.content ? (
            <>
              <div className="text-gray-200 whitespace-pre-wrap leading-relaxed">
                {parseLinksFromText(sectionData.content, primaryColor)}
              </div>
              {sectionData.expandableLink && (
                <div className="pt-4 text-center">
                  <a
                    href={sectionData.expandableLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 text-white rounded-lg transition-colors font-medium hover:opacity-95"
                    style={{
                      backgroundColor: primaryColor,
                      boxShadow: `0 8px 20px ${hexToRgba(primaryColor, 0.3)}`
                    }}
                  >
                    <span>{sectionData.expandableLinkLabel || 'Learn More'}</span>
                    <i className="fas fa-external-link-alt ml-2"></i>
                  </a>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <i className="fas fa-puzzle-piece text-4xl text-gray-400 mb-4"></i>
              <p className="text-gray-400">No content added to this section yet.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMoreTab = () => (
    <div className="max-w-4xl mx-auto px-4 py-8">
     

      <div className="space-y-3">
        {customSectionsList.map(customSection => {
          const sectionData = customSection.data;

          // If it's a link type, make it clickable directly
          if (sectionData.type === 'link') {
            return (
              <a
                key={customSection.id}
                href={formatUrl(sectionData.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-gray-800 hover:bg-gray-700 rounded-lg p-4 shadow-md border-2 transition-all group"
                style={{ borderColor: primaryColor }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <i className={`fas ${customSection.icon} text-lg`}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white group-hover:opacity-90 transition">
                        {customSection.label}
                      </h4>
                    </div>
                  </div>
                  <i className="fas fa-external-link-alt text-gray-400 group-hover:text-white transition"></i>
                </div>
              </a>
            );
          }

          // For content sections, show as expandable
          return (
            <div key={customSection.id} className="bg-gray-800 rounded-lg border-2 overflow-hidden" style={{ borderColor: primaryColor }}>
              {/* Clickable Header */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <i className={`fas ${customSection.icon} text-lg`}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">
                        {customSection.label}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Area */}
              {sectionData.content && (
                <div className="px-4 pb-4">
                  <div className="bg-gray-700 rounded-lg p-4 text-gray-200 whitespace-pre-wrap leading-relaxed">
                    {parseLinksFromText(sectionData.content, primaryColor)}
                  </div>
                  {sectionData.expandableLink && (
                    <div className="mt-4 text-center">
                      <a
                        href={formatUrl(sectionData.expandableLink)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 text-white rounded-lg transition-colors font-medium hover:opacity-95"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <span>{sectionData.expandableLinkLabel || 'Learn More'}</span>
                        <i className="fas fa-external-link-alt ml-2"></i>
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Referral Section */}
        {enhancedToggles.showReferral && profileData.referral?.enabled && (
          <a
            href={`${typeof window !== 'undefined' ? window.location.origin : 'https://chessweb.site'}/me?ref=${profileData.userId || profileData.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-gray-800 hover:bg-gray-700 rounded-lg p-4 shadow-md border-2 transition-all group"
            style={{ borderColor: primaryColor }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  <i className="fas fa-rocket text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-white group-hover:opacity-90 transition">
                    {profileData.referral?.ctaText || 'Create Your Page'}
                  </h4>
                </div>
              </div>
              <i className="fas fa-arrow-right text-gray-400 group-hover:text-white transition"></i>
            </div>
          </a>
        )}
      </div>
    </div>
  );

  const renderTabContent = (tabId) => {
    switch (tabId) {
      case 'home':
        return renderHomeTab();
      case 'about':
        return renderAboutTab();
      case 'achievements':
        return renderAchievementsTab();
      case 'coaching':
        return renderCoachingTab();
      case 'photos':
        return renderPhotosTab();
      case 'youtube':
        return renderYouTubeTab();
      case 'lichessStudies':
        return renderLichessStudiesTab();
      case 'goals':
        return renderGoalsTab();
      case 'chessGame':
        return renderChessGameTab();
      case 'blog':
        return renderBlogTab();
      case 'affiliateLinks':
        return renderAffiliateLinksTab();
      case 'shop':
        return renderShopTab();
      case 'contact':
        return renderContactTab();
      case 'referral':
        return renderReferralTab();
      case 'more':
        return renderMoreTab();
      default:
        return null;
    }
  };

  if (sortedTabs.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">No sections available for this profile.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-hidden">
      {/* Sticky Header */}
      {isMobile ? (
        // MOBILE HEADER - Vertical Stack
        <div className="sticky top-0 z-50 bg-gradient-to-b from-gray-900 via-gray-900 to-gray-900/95 backdrop-blur-md border-b border-gray-800 shadow-lg px-4 py-5">
          {/* Profile Section - Centered */}
          <div className="flex flex-col items-center gap-3 ">
            {/* Profile Photo */}
            {profileData.photoUrl ? (
              <img
                src={profileData.photoUrl}
                alt={profileData.displayName}
                className="w-16 h-16 rounded-full object-cover shadow-lg flex-shrink-0"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gray-700 shadow-lg flex items-center justify-center flex-shrink-0">
                <i className="fas fa-user text-gray-400 text-2xl"></i>
              </div>
            )}

            {/* Name + Badges */}
            <button
              onClick={handleCopyLink}
              className="text-xl font-bold text-white hover:opacity-90 transition cursor-pointer flex items-center gap-2"
              title="Copy profile link"
              style={{
                textShadow: `0 0 15px ${hexToRgba(primaryColor, 0.4)}`,
              }}
            >
              <span>{profileData.displayName || "Chess Player"}</span>

              {(profileData.titleCEO || profileData.fideTitle) && (
                <span
                  className={`${
                    profileData.titleCEO || profileData.fideTitle === "CEO"
                      ? "bg-yellow-500 text-black"
                      : profileData.username === "charlize"
                      ? "text-white hover:opacity-90"
                      : "bg-red-600 text-white hover:bg-red-700"
                  } px-2 py-1 rounded text-xs font-bold transition-colors`}
                  style={
                    profileData.titleCEO || profileData.fideTitle === "CEO"
                      ? { backgroundColor: "#ffd700" }
                      : profileData.username === "charlize"
                      ? { backgroundColor: "#f980a7" }
                      : {}
                  }
                  title={
                    profileData.titleCEO
                      ? "CEO of Chess Website"
                      : titleFullNames[profileData.fideTitle] ||
                        profileData.fideTitle
                  }
                >
                  {profileData.titleCEO ? "CEO" : profileData.fideTitle}
                </span>
              )}

              {/* Profile Type Badge */}
              {profileType && profileTypeConfig[profileType] && (
                <span
                  className="bg-red-600 text-white hover:bg-red-700 px-2 py-1 rounded text-xs font-bold transition-colors flex items-center gap-1"
                  title={profileTypeConfig[profileType].label}
                >
                  <i className={`fas ${profileTypeConfig[profileType].icon} text-[10px]`}></i>
                  <span>{profileTypeConfig[profileType].label}</span>
                </span>
              )}

              {profileData.playerPremium ? (
                <i
                  className="fas fa-crown text-lg"
                  style={{ color: primaryColor }}
                ></i>
              ) : (
                <i
                  className="fas fa-link text-lg"
                  style={{ color: primaryColor }}
                ></i>
              )}

              {/* Copy Feedback */}
              {copyFeedback && (
                <span className="text-xs text-green-400 animate-pulse">
                  {copyFeedback}
                </span>
              )}
            </button>

            {/* Social Icons */}
            <div className="flex items-center justify-center">
              <SocialLinksBubbles
                socialLinks={profileData.socialLinks || {}}
                chessLinks={profileData.links || {}}
                primarySocialProfile={profileData.primarySocialProfile}
                onChessPlatformClick={handleChessPlatformClick}
                onDonationClick={handleDonationClick}
                formData={profileData}
              />
            </div>
          </div>

          {/* Menu Button - Below profile */}
          {sortedTabs.length > 1 && (
            <div className="flex items-center justify-center">
              <div className="relative">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="flex items-center gap-2 px-4 py-2 text-white font-semibold text-sm rounded-lg transition-all"
                  style={{
                    background: "rgba(31, 41, 55, 0.8)",
                    border: `2px solid ${primaryColor}`,
                  }}
                >
                  Menu
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${
                      isMobileMenuOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {isMobileMenuOpen && (
                  <div
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-gray-800 rounded-lg shadow-xl border border-gray-700 py-2 min-w-[200px] z-50"
                    style={{ maxHeight: "400px", overflowY: "auto" }}
                  >
                    {sortedTabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => {
                          setActiveTab(tab.id);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 transition-colors ${
                          activeTab === tab.id
                            ? "bg-gray-700 text-white font-semibold"
                            : "text-gray-300 hover:bg-gray-700"
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        // DESKTOP HEADER - Horizontal Layout (UNCHANGED)
        <div className={`fixed ${isEditorPreview ? 'top-[120px]' : 'top-0'} left-0 right-0 z-50 bg-gradient-to-b from-gray-900 via-gray-900 to-gray-900/95 backdrop-blur-md border-b border-gray-800 shadow-lg flex justify-between px-10 py-5`}>
          {/* Left Side: Photo + Name/Badges + Social Icons */}
          <div className="flex items-center gap-4">
            {/* Profile Photo */}
            {profileData.photoUrl ? (
              <img
                src={profileData.photoUrl}
                alt={profileData.displayName}
                className="w-24 h-24 rounded-full object-cover shadow-lg flex-shrink-0"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gray-700 shadow-lg flex items-center justify-center flex-shrink-0">
                <i className="fas fa-user text-gray-400 text-3xl"></i>
              </div>
            )}

            {/* Name/Badge and Social Icons - Two Rows */}
            <div className="flex flex-col items-start justify-between gap-2 h-24 ml-5">
              {/* Top Row: Name + Badges */}
              <button
                onClick={handleCopyLink}
                className="text-3xl font-bold text-white hover:opacity-90 transition cursor-pointer flex items-center gap-2"
                title="Copy profile link"
                style={{
                  textShadow: `0 0 15px ${hexToRgba(primaryColor, 0.4)}`,
                }}
              >
                <span>{profileData.displayName || "Chess Player"}</span>

                {(profileData.titleCEO || profileData.fideTitle) && (
                  <span
                    className={`${
                      profileData.titleCEO || profileData.fideTitle === "CEO"
                        ? "bg-yellow-500 text-black"
                        : profileData.username === "charlize"
                        ? "text-white hover:opacity-90"
                        : "bg-red-600 text-white hover:bg-red-700"
                    } px-2 py-1 rounded text-xs font-bold transition-colors`}
                    style={
                      profileData.titleCEO || profileData.fideTitle === "CEO"
                        ? { backgroundColor: "#ffd700" }
                        : profileData.username === "charlize"
                        ? { backgroundColor: "#f980a7" }
                        : {}
                    }
                    title={
                      profileData.titleCEO
                        ? "CEO of Chess Website"
                        : titleFullNames[profileData.fideTitle] ||
                          profileData.fideTitle
                    }
                  >
                    {profileData.titleCEO ? "CEO" : profileData.fideTitle}
                  </span>
                )}

                {/* Profile Type Badge */}
                {profileType && profileTypeConfig[profileType] && (
                  <span
                    className="bg-red-600 text-white hover:bg-red-700 px-2 py-1 rounded text-xs font-bold transition-colors flex items-center gap-1"
                    title={profileTypeConfig[profileType].label}
                  >
                    <i className={`fas ${profileTypeConfig[profileType].icon} text-[10px]`}></i>
                    <span>{profileTypeConfig[profileType].label}</span>
                  </span>
                )}

                {profileData.playerPremium ? (
                  <i
                    className="fas fa-crown text-lg"
                    style={{ color: primaryColor }}
                  ></i>
                ) : (
                  <i
                    className="fas fa-link text-lg"
                    style={{ color: primaryColor }}
                  ></i>
                )}

                {/* Copy Feedback */}
                {copyFeedback && (
                  <span className="text-xs text-green-400 animate-pulse">
                    {copyFeedback}
                  </span>
                )}
              </button>

              {/* Bottom Row: Social Icons */}
              <div className="flex items-start justify-start">
                <SocialLinksBubbles
                  socialLinks={profileData.socialLinks || {}}
                  chessLinks={profileData.links || {}}
                  primarySocialProfile={profileData.primarySocialProfile}
                  onChessPlatformClick={handleChessPlatformClick}
                  onDonationClick={handleDonationClick}
                  formData={profileData}
                />
              </div>
            </div>
          </div>

          {/* Right Side: Tab Navigation */}
          {sortedTabs.length > 1 && (
            <div className="flex items-center">
              <nav className="flex space-x-1">
                {sortedTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`py-2 px-3 border-2 font-semibold text-sm transition-all rounded-lg duration-200 whitespace-nowrap ${
                      activeTab === tab.id
                        ? "text-white rounded-lg"
                        : "text-white border-transparent hover:bg-gray-700"
                    }`}
                    style={
                      activeTab === tab.id
                        ? {
                            background: `linear-gradient(to right, ${primaryColor}, ${primaryColor}90)`,
                            border: `2px solid ${primaryColor}`,
                          }
                        : {
                            border: "2px solid transparent",
                          }
                    }
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          )}
        </div>
      )}

      {/* Content Area */}
      <div className={`max-w-6xl mx-auto px-2 md:px-4 pt-4 pb-4 md:pb-8 ${isEditorPreview ? 'md:pt-[160px]' : 'md:pt-[160px]'}`}>
        {/* Chess Platform Bubble Tab */}
        {activeChessPlatform && (
          <ChessPlatformBubbleTab
            platform={activeChessPlatform}
            username={profileData.links?.[activeChessPlatform]}
            onClose={() => setActiveChessPlatform(null)}
            accentColor={primaryColor}
          />
        )}

        {/* Donation Bubble Tab */}
        {activeDonation && (
          <DonationBubbleTab
            profileId={profileId}
            profileName={profileData.displayName || profileData.username}
            donationMessage={profileData.donationMessage}
            onClose={() => setActiveDonation(false)}
            accentColor={primaryColor}
            isOwner={isOwner}
            profileUsername={profileData.username || profileData.slug}
          />
        )}

        {/* Tab Content */}
        <div>{renderTabContent(activeTab)}</div>
      </div>
    </div>
  );
};

export default PlayerProfilePreviewTabs;
